import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BookOpen,
  Loader2,
  CheckCircle2,
  XCircle,
  MinusCircle,
  AlertCircle,
  Play,
  ChevronDown,
  ChevronUp,
  RotateCcw,
} from 'lucide-react';
import {
  applyRedlineWithTrackChanges,
  scrollToText,
} from '../utils/wordApi';
import {
  getEditStatuses,
  subscribeDocumentChanges,
  markDocumentChanged,
} from '../utils/changeStatus';
import { resolveContractContext } from '../utils/contractResolver';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const API_TOKEN = import.meta.env.VITE_ADDIN_API_TOKEN || '';

interface ContractCtx {
  contractId: string;
  tenantId: string;
}

interface PlaybookSummary {
  id: string;
  title: string;
  description: string | null;
  is_active: boolean;
  contract_types: { id: string; name: string } | null;
  _count: { rules: number };
}

interface RuleSummary {
  id: string;
  name: string;
  prompt_text: string;
}

interface RedlineEntry {
  anchor_text: string | null;
  current_value: string | null;
  suggested_replacement: string | null;
}

interface PendingResult {
  id: string;
  status: 'failed';
  reason: string | null;
  anchor_text: string | null;
  current_value: string | null;
  suggested_replacement: string | null;
  redlines: RedlineEntry[] | null;
  confidence: number | null;
  redline_applied: boolean;
  rule: RuleSummary;
}

interface RunResult {
  id: string;
  status: 'passed' | 'failed' | 'not_applicable' | 'error';
  reason: string | null;
  anchor_text: string | null;
  current_value: string | null;
  suggested_replacement: string | null;
  redlines: RedlineEntry[] | null;
  redline_applied: boolean;
  rule: RuleSummary;
}

interface RunSummary {
  id: string;
  status: string;
  passed_count: number;
  failed_count: number;
  not_applicable_count: number;
  playbook: { id: string; title: string };
  results?: RunResult[];
}

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'x-addin-token': API_TOKEN,
    'ngrok-skip-browser-warning': 'true',
  } as Record<string, string>;
}

export function Playbook() {
  // Tenant context is needed to fetch the latest pending run; we don't show
  // the contract id directly so it's unused after detection.
  const [, setContractCtx] = useState<ContractCtx | null>(null);
  // Track for diagnostics — we read tenant playbooks only to confirm the
  // tenant has any active playbook configured.
  const [playbooks, setPlaybooks] = useState<PlaybookSummary[]>([]);
  const [loadingPlaybooks, setLoadingPlaybooks] = useState(true);
  const [run, setRun] = useState<RunSummary | null>(null);
  const [results, setResults] = useState<RunResult[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [applyErrors, setApplyErrors] = useState<Record<string, string>>({});
  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingResult[]>([]);
  // Applied redlines the user has since RESOLVED in Word (accepted or rejected —
  // the tracked change is gone). Like the Legal Connection Applied Fixes panel,
  // a resolved change drops out of the taskpane. Rebuilt from the document on
  // every reconcile, so it survives reloads without server state.
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  // resultId -> timestamp of the last apply. A redline applied within the last
  // few seconds is never auto-un-applied by reconciliation: Word's body.text
  // interleaves the struck-old + inserted-new text of a fresh tracked change,
  // which can momentarily look like the original is "back". The grace window
  // lets the tracked change settle before we trust a rejected verdict.
  const recentlyAppliedRef = useRef<Map<string, number>>(new Map());
  const APPLY_GRACE_MS = 8000;
  // Detection info is captured for diagnostics but the UI no longer shows
  // the contract type banner — per user request.
  const [, setDetectedType] = useState<{
    id: string | null;
    name: string | null;
    detected: boolean;
  } | null>(null);

  // Keep the bottom-nav "open redlines" badge (in App.tsx) in sync whenever the
  // applied state changes here. App only listens for `ezylex:redlines-updated`,
  // so without this the badge count drifts from what this pane shows after an
  // apply or a reconcile un-apply.
  const emitOpenCount = (list: Array<{ redline_applied: boolean }>) => {
    const open = list.filter((r) => !r.redline_applied).length;
    try {
      window.dispatchEvent(
        new CustomEvent('ezylex:redlines-updated', { detail: { openRedlines: open } })
      );
    } catch {}
  };

  // Resolve contract context, auto-detect playbooks, and check pending redlines
  useEffect(() => {
    (async () => {
      try {
        const ctx = await resolveContractContext();
        if (!ctx || !ctx.tenantId) {
          setError('Could not detect tenant. Open this document from EzyLex.');
          setLoadingPlaybooks(false);
          return;
        }
        setContractCtx({ contractId: ctx.contractId || '', tenantId: ctx.tenantId });

        const hasContractId = !!ctx.contractId;

        if (hasContractId) {
          // Auto-detect playbooks bound to this contract's type
          const res = await fetch(
            `${API_BASE_URL}/api/contracts/${ctx.contractId}/playbook`,
            { headers: authHeaders() }
          );
          if (res.ok) {
            const data = await res.json();
            const pbs = (data.playbooks || []) as PlaybookSummary[];
            setPlaybooks(pbs);
            setDetectedType({
              id: data.contract_type_id ?? null,
              name: data.contract_type_name ?? null,
              detected: !!data.contract_type_detected,
            });
          } else {
            setError(`Failed to load playbooks (${res.status})`);
          }

          // Pending redlines from a previous run
          const runIdHint = (() => {
            try {
              const match = document.cookie.match(/ezylex-playbook-run-id=([^;]+)/);
              return match ? match[1] : null;
            } catch { return null; }
          })();
          const pendingUrl = runIdHint
            ? `${API_BASE_URL}/api/contracts/${ctx.contractId}/pending-redlines?runId=${encodeURIComponent(runIdHint)}`
            : `${API_BASE_URL}/api/contracts/${ctx.contractId}/pending-redlines`;
          const pendingRes = await fetch(pendingUrl, { headers: authHeaders() });
          if (pendingRes.ok) {
            const data = await pendingRes.json();
            // Prefer the full results list (includes redlines the user already
            // applied) so the pane keeps showing the run with per-item applied
            // state instead of reverting to the empty "Run playbook" prompt.
            // Fall back to `pending` for older backends.
            const items =
              data.results && data.results.length > 0
                ? data.results
                : data.pending || [];
            if (data.run && items.length > 0) {
              setPending(items);
              emitOpenCount(items);
              setRun({
                id: data.run.id,
                status: 'completed',
                passed_count: 0,
                failed_count: data.run.failed_count,
                not_applicable_count: 0,
                playbook: data.run.playbook,
              });
            }
          }
        } else {
          // No contract id resolved (e.g. doc was opened outside the EzyLex
          // edit flow, or it's a third-party docx). Fall back to listing
          // all tenant playbooks; user picks one manually. The contract type
          // banner shows "Unknown".
          const res = await fetch(
            `${API_BASE_URL}/api/cms/playbooks?tenantId=${encodeURIComponent(ctx.tenantId)}&isActive=true`,
            { headers: authHeaders() }
          );
          if (res.ok) {
            const data = await res.json();
            const pbs = (data.playbooks || []) as PlaybookSummary[];
            setPlaybooks(pbs);
            setDetectedType({ id: null, name: null, detected: false });
          } else {
            setError(`Failed to load playbooks (${res.status})`);
          }

          // Also try to auto-load the latest pending playbook run for this
          // tenant. Useful when the user clicked "Edit in Word & Apply
          // Redlines" from the CMS modal — the cookie that carries the
          // runId is on lc.drupallers.com so the addin (loaded from
          // addin.ezylex.ai) can't read it. This server-side lookup gives
          // us the run by tenant.
          try {
            const pendingRes = await fetch(
              `${API_BASE_URL}/api/addin/latest-pending-playbook-run?tenantId=${encodeURIComponent(ctx.tenantId)}`,
              { headers: authHeaders() }
            );
            if (pendingRes.ok) {
              const data = await pendingRes.json();
              if (data.pending && data.pending.length > 0 && data.run) {
                setPending(data.pending);
                emitOpenCount(data.pending);
                setRun({
                  id: data.run.id,
                  status: 'completed',
                  passed_count: 0,
                  failed_count: data.run.failed_count,
                  not_applicable_count: 0,
                  playbook: data.run.playbook,
                });
              }
            }
          } catch {
            // Non-fatal; user can still run a playbook manually
          }
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoadingPlaybooks(false);
        // Reconcile applied redlines against the document on (re)open: a redline
        // the user rejected in a previous session still reads redline_applied on
        // the server, so without this it would wrongly show "Applied" instead of
        // the Apply button. The grace window is empty on a fresh mount, so a
        // genuinely rejected redline is correctly un-applied here.
        setTimeout(() => reconcileRef.current(), 1200);
      }
    })();
  }, []);

  const handleApplyAllRedlines = async (
    items: Array<{
      id: string;
      anchor_text: string | null;
      suggested_replacement: string | null;
      redlines: RedlineEntry[] | null;
      redline_applied: boolean;
    }>
  ) => {
    if (!run) return;
    setError(null);
    setApplyErrors({});

    // Flatten every result into its constituent redline pairs. Many rules
    // now produce multiple redlines (e.g. confidentiality term that
    // applies to BOTH the term clause and the survival clause). We apply
    // each in turn, then mark the parent result applied if ALL pairs
    // succeeded.
    type Job = {
      resultId: string;
      anchor: string;
      replacement: string;
      isLastForResult: boolean;
    };
    const jobs: Job[] = [];
    for (const r of items) {
      if (r.redline_applied) continue;
      const pairs: RedlineEntry[] =
        r.redlines && r.redlines.length > 0
          ? r.redlines
          : r.anchor_text && r.suggested_replacement
          ? [
              {
                anchor_text: r.anchor_text,
                suggested_replacement: r.suggested_replacement,
                current_value: null,
              },
            ]
          : [];
      const usable = pairs.filter(
        (p) => p.anchor_text && p.suggested_replacement
      );
      usable.forEach((p, idx) => {
        jobs.push({
          resultId: r.id,
          anchor: p.anchor_text as string,
          replacement: p.suggested_replacement as string,
          isLastForResult: idx === usable.length - 1,
        });
      });
    }

    if (jobs.length === 0) return;

    const perResultFailures: Record<string, string[]> = {};
    const perResultSuccesses: Record<string, number> = {};
    let totalSuccesses = 0;

    for (const job of jobs) {
      setApplyingId(job.resultId);
      const apply = await applyRedlineWithTrackChanges(job.anchor, job.replacement);
      if (apply.success) {
        totalSuccesses++;
        perResultSuccesses[job.resultId] = (perResultSuccesses[job.resultId] || 0) + 1;
      } else {
        (perResultFailures[job.resultId] ||= []).push(
          `"${job.anchor}": ${apply.error || 'not found'}`
        );
      }

      // Mark the result applied only once all of its redlines have
      // succeeded (or this was the only one and it failed — we then
      // surface the error rather than marking applied).
      if (job.isLastForResult) {
        const hadFailures = (perResultFailures[job.resultId] || []).length > 0;
        if (!hadFailures) {
          recentlyAppliedRef.current.set(job.resultId, Date.now());
          try {
            await fetch(
              `${API_BASE_URL}/api/playbook-runs/${run.id}/results/${job.resultId}/applied`,
              { method: 'POST', headers: authHeaders() }
            );
          } catch {}
          setResults((prev) =>
            prev.map((r) =>
              r.id === job.resultId ? { ...r, redline_applied: true } : r
            )
          );
          setPending((prev) =>
            prev.map((p) =>
              p.id === job.resultId ? { ...p, redline_applied: true } : p
            )
          );
        }
      }
    }

    setApplyingId(null);

    // Refresh the bottom-nav open-redline badge from the freshest state (this
    // no-op updater runs after all the per-result applied updates above).
    setPending((prev) => {
      emitOpenCount(prev);
      return prev;
    });

    // The document changed — let the Risk Analysis pane / Home gauge know so
    // they can flag their view as possibly out of date. Persisted (not just a
    // live event) because the Risk pane is unmounted while this one is active.
    if (totalSuccesses > 0) {
      markDocumentChanged();
    }

    const errMap: Record<string, string> = {};
    for (const [rid, msgs] of Object.entries(perResultFailures)) {
      errMap[rid] = msgs.join('; ');
    }
    setApplyErrors(errMap);

    const totalFailures = Object.values(perResultFailures).reduce(
      (n, msgs) => n + msgs.length,
      0
    );
    if (totalFailures > 0) {
      setError(
        `Applied ${totalSuccesses} of ${jobs.length} redline${
          jobs.length === 1 ? '' : 's'
        }. ${totalFailures} could not be located in the document — see details below.`
      );
    }
  };

  // Reconcile applied redlines against the live document. If the user REJECTED
  // an applied redline in Word's Review pane, its original text is back — we
  // un-apply it (locally + on the server) so its Apply button returns.
  const reconcileAppliedRedlines = useCallback(async () => {
    if (!run) return;
    const items = pending.length > 0 ? pending : results;
    // Only look at applied redlines we still show (not already resolved).
    const applied = items.filter((r) => r.redline_applied && !resolvedIds.has(r.id));
    if (applied.length === 0) return;

    const edits = applied
      .map((r) => ({
        id: r.id,
        anchor: (r.anchor_text || r.current_value || '') as string,
        rewritten: (r.suggested_replacement || '') as string,
      }))
      .filter((e) => e.rewritten && e.anchor);
    if (edits.length === 0) return;

    let statuses;
    try {
      statuses = await getEditStatuses(edits);
    } catch {
      return;
    }

    // Ignore just-applied redlines: a fresh tracked change can momentarily read
    // as its own original (struck + inserted text interleave in body.text).
    const now = Date.now();
    for (const [id, ts] of recentlyAppliedRef.current) {
      if (now - ts > APPLY_GRACE_MS) recentlyAppliedRef.current.delete(id);
    }
    // Legal Connection model: once the user RESOLVES an applied change in Word
    // (accepts OR rejects — either way the tracked change is gone, so status is
    // no longer 'pending'), drop that row from the taskpane.
    const nowResolved = statuses
      .filter((s) => s.status !== 'pending')
      .filter((s) => {
        const ts = recentlyAppliedRef.current.get(s.id);
        return !ts || now - ts > APPLY_GRACE_MS;
      })
      .map((s) => s.id);
    if (nowResolved.length === 0) return;

    setResolvedIds((prev) => {
      const next = new Set(prev);
      nowResolved.forEach((id) => next.add(id));
      return next;
    });
    markDocumentChanged();
  }, [run, pending, results, resolvedIds]);

  // Register the change listeners once; a ref keeps them pointed at the latest
  // reconcile closure so they always see fresh applied state.
  const reconcileRef = useRef<() => void>(() => {});
  reconcileRef.current = () => { void reconcileAppliedRedlines(); };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const debounced = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => reconcileRef.current(), 400);
    };
    const onFocus = () => reconcileRef.current();
    const onVisible = () => {
      if (document.visibilityState === 'visible') reconcileRef.current();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const unsub = subscribeDocumentChanges(debounced);
    // Poll as a guaranteed fallback: Word Online does not reliably fire
    // selection/paragraph events when the user clicks Reject/Accept in the
    // Review ribbon, so the event triggers can miss it. Cheap — the reconcile
    // no-ops when nothing is applied, and is one Word.run otherwise.
    const poll = setInterval(() => reconcileRef.current(), 3000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      unsub();
      if (timer) clearTimeout(timer);
      clearInterval(poll);
    };
  }, []);

  const toggleExpand = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const renderResultCard = (r: RunResult | PendingResult, opts?: { compact?: boolean }) => {
    const isOpen = expanded.has(r.id);
    const failed = r.status === 'failed';
    return (
      <div
        key={r.id}
        className="bg-white border rounded p-3"
      >
        <div className="flex items-start gap-2">
          <div className="mt-0.5 flex-shrink-0">
            {r.status === 'passed' && <CheckCircle2 size={16} className="text-green-600" />}
            {r.status === 'failed' && <XCircle size={16} className="text-red-600" />}
            {r.status === 'not_applicable' && <MinusCircle size={16} className="text-gray-400" />}
            {r.status === 'error' && <AlertCircle size={16} className="text-orange-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-gray-900">{r.rule.name}</div>
            {!opts?.compact && (
              <div className="text-xs text-gray-500 mt-0.5 truncate">
                {r.rule.prompt_text}
              </div>
            )}
            {r.reason && (
              <div className="text-xs text-gray-700 mt-1">{r.reason}</div>
            )}

            {failed && r.anchor_text && (
              <button type="button"
                onClick={() => toggleExpand(r.id)}
                className="mt-2 text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {isOpen ? 'Hide details' : 'Show details'}
              </button>
            )}

            {failed && isOpen && r.anchor_text && (
              <div className="mt-2 space-y-2">
                <div>
                  <div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">
                    Current text
                  </div>
                  <div className="text-xs bg-red-50 border-l-2 border-red-400 px-2 py-1 rounded">
                    {r.anchor_text}
                  </div>
                  <button type="button"
                    onClick={() => scrollToText(r.anchor_text || '').catch(() => {})}
                    className="text-[10px] text-blue-600 hover:underline mt-1"
                  >
                    Scroll to text
                  </button>
                </div>
                {r.suggested_replacement && (
                  <div>
                    <div className="text-[10px] uppercase text-gray-500 font-semibold mb-1">
                      Suggested replacement
                    </div>
                    <div className="text-xs bg-green-50 border-l-2 border-green-400 px-2 py-1 rounded">
                      {r.suggested_replacement}
                    </div>
                  </div>
                )}
              </div>
            )}

            {r.redline_applied && (
              <div className="mt-2 text-xs text-green-700 inline-flex items-center gap-1">
                <CheckCircle2 size={12} /> Applied as tracked change
              </div>
            )}

            {applyErrors[r.id] && !r.redline_applied && (
              <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
                <span className="font-medium">Could not apply: </span>
                {applyErrors[r.id]}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // A redline that WAS applied (tracked change in the doc). Shows a one-line
  // summary + a Jump button; the user reviews it in Word and Accept/Reject
  // clears it from this list (see reconcileAppliedRedlines).
  const renderAppliedCard = (r: RunResult | PendingResult) => {
    const summary = r.reason || r.rule.prompt_text || '';
    const jumpTarget = r.suggested_replacement || r.anchor_text || '';
    return (
      <div key={r.id} className="bg-white border rounded p-3">
        <div className="flex items-start gap-2">
          <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-gray-900">{r.rule.name}</div>
            {summary && (
              <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{summary}</div>
            )}
            <div className="mt-2 flex items-center gap-3">
              {jumpTarget && (
                <button
                  type="button"
                  onClick={() => scrollToText(jumpTarget).catch(() => {})}
                  className="text-xs font-medium text-blue-600 hover:underline"
                >
                  Jump to change →
                </button>
              )}
              <span className="text-[11px] text-green-700 inline-flex items-center gap-1">
                <CheckCircle2 size={11} /> Applied
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // A redline the playbook produced but that could NOT be applied — the anchor
  // wasn't found (or matched ambiguously) in the current document.
  const renderFailedCard = (r: RunResult | PendingResult) => (
    <div key={r.id} className="bg-white border rounded p-3">
      <div className="flex items-start gap-2">
        <XCircle size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm text-gray-900">{r.rule.name}</div>
          <div className="text-xs text-red-700 mt-0.5">
            Couldn&apos;t apply — {applyErrors[r.id] || 'anchor not found in the document'}
          </div>
          {r.suggested_replacement && (
            <div className="mt-1 text-[11px] text-gray-500">
              <span className="font-medium">Suggested:</span> {r.suggested_replacement}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loadingPlaybooks) {
    return (
      <div className="p-6 text-center text-gray-500">
        <Loader2 className="animate-spin inline mr-2" size={18} /> Loading playbooks…
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <BookOpen className="text-blue-600" size={20} />
        <h2 className="font-semibold text-gray-900">Redlining</h2>
        {run && (
          <button
            type="button"
            onClick={() => reconcileRef.current()}
            title="Refresh — check for redlines you accepted or rejected in Word"
            className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600"
          >
            <RotateCcw size={13} />
            Refresh
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
          {error}
        </div>
      )}

      {(() => {
        const items = pending.length > 0 ? pending : results;

        if (items.length === 0) {
          if (playbooks.length === 0) {
            return (
              <div className="text-sm text-gray-500 text-center py-8 border rounded p-4">
                No playbooks are bound to this contract type. Create one in
                EzyLex (Contract Types → Playbooks), then reopen this pane.
              </div>
            );
          }
          return (
            <div className="border rounded p-4 space-y-2 bg-gray-50 text-sm text-gray-600">
              <div className="font-medium text-gray-800">No redlines to apply yet.</div>
              <p>
                Run the playbook from your EzyLex workspace — open the contract,
                choose <span className="font-medium">Run Playbook</span>, then{' '}
                <span className="font-medium">Edit in Word &amp; Apply Redlines</span>.
                The suggested redlines will appear here ready to apply.
              </p>
            </div>
          );
        }

        // Three buckets, mirroring the Legal Connection Applied Fixes panel.
        const toApply = items.filter(
          (r) =>
            !r.redline_applied &&
            !applyErrors[r.id] &&
            r.anchor_text &&
            r.suggested_replacement
        );
        const appliedPending = items.filter(
          (r) => r.redline_applied && !resolvedIds.has(r.id)
        );
        const failed = items.filter((r) => !r.redline_applied && !!applyErrors[r.id]);

        if (toApply.length === 0 && appliedPending.length === 0 && failed.length === 0) {
          return (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
              <CheckCircle2 size={16} /> All redlines have been resolved.
            </div>
          );
        }

        return (
          <div className="space-y-4">
            {toApply.length > 0 && (
              <div className="space-y-2">
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
                  {toApply.length} redline{toApply.length === 1 ? '' : 's'} ready to
                  apply from the latest playbook run.
                </div>
                <button
                  type="button"
                  onClick={() => handleApplyAllRedlines(toApply as any)}
                  disabled={!!applyingId}
                  className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-60"
                >
                  {applyingId ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Play size={16} />
                  )}
                  Apply Redlining
                </button>
                {toApply.map((r) => renderResultCard(r as RunResult, { compact: true }))}
              </div>
            )}

            {appliedPending.length > 0 && (
              <div className="space-y-2">
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
                  {appliedPending.length} redline{appliedPending.length === 1 ? '' : 's'}{' '}
                  applied as tracked changes · Jump to review each, then{' '}
                  <span className="font-medium">Accept</span> or{' '}
                  <span className="font-medium">Reject</span> in Word to clear it here.
                </div>
                {appliedPending.map((r) => renderAppliedCard(r))}
              </div>
            )}

            {failed.length > 0 && (
              <div className="space-y-2">
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                  {failed.length} redline{failed.length === 1 ? '' : 's'} couldn&apos;t be
                  applied — the anchor text wasn&apos;t found in the document.
                </div>
                {failed.map((r) => renderFailedCard(r))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
